import React from 'react';
import Icon from 'components/AppIcon';
import { SUBSCRIPTION_TIERS } from './SubscriptionTierBadge';

const BASE_COMPLEXITY_OPTIONS = [
  { value: 'simple', label: 'Simple', icon: 'Minus' },
  { value: 'medium', label: 'Medium', icon: 'Equal' },
  { value: 'complex', label: 'Complex', icon: 'Plus' },
];

function getDurationLabel(complexity, tier) {
  const tierData = SUBSCRIPTION_TIERS?.[tier] || SUBSCRIPTION_TIERS?.free;
  return tierData?.durations?.[complexity] || '~30s';
}

export default function GenerationSettings({ complexity, onComplexityChange, safeMode, onSafeModeChange, disabled, tier = 'free', onUpgrade }) {
  const tierData = SUBSCRIPTION_TIERS?.[tier] || SUBSCRIPTION_TIERS?.free;

  return (
    <div className="flex flex-col gap-4">
      {/* Complexity */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <p className="text-sm font-semibold" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>Complexity</p>
          {/* Tier badge inline */}
          <button
            onClick={onUpgrade}
            className="flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold transition-all hover:opacity-80"
            style={{ background: `${tierData?.color}18`, color: tierData?.color, border: `1px solid ${tierData?.color}40`, fontFamily: 'var(--font-caption)' }}
            title="Click to upgrade plan"
          >
            <Icon name={tierData?.icon} size={10} color={tierData?.color} />
            {tierData?.name}
          </button>
        </div>
        <div className="flex gap-2">
          {BASE_COMPLEXITY_OPTIONS?.map((opt) => {
            const active = complexity === opt?.value;
            const durationLabel = getDurationLabel(opt?.value, tier);
            return (
              <button
                key={opt?.value}
                onClick={() => !disabled && onComplexityChange(opt?.value)}
                disabled={disabled}
                className="flex-1 flex flex-col items-center gap-1 py-2 px-1 rounded-lg border text-xs transition-all disabled:opacity-50"
                style={{
                  borderColor: active ? 'var(--color-primary)' : 'var(--color-border)',
                  background: active ? 'rgba(99,102,241,0.1)' : 'var(--color-muted)',
                  color: active ? 'var(--color-primary)' : 'var(--color-muted-foreground)',
                  fontFamily: 'var(--font-caption)',
                }}
              >
                <Icon name={opt?.icon} size={14} color={active ? 'var(--color-primary)' : 'var(--color-muted-foreground)'} />
                <span className="font-semibold">{opt?.label}</span>
                <span style={{ color: active ? tierData?.color : 'var(--color-muted-foreground)', fontWeight: active ? 700 : 400 }}>{durationLabel}</span>
              </button>
            );
          })}
        </div>
        {/* Upgrade hint for free tier */}
        {tier === 'free' && (
          <button
            onClick={onUpgrade}
            className="mt-2 w-full flex items-center justify-center gap-1.5 py-1.5 rounded-lg text-xs transition-all hover:opacity-80"
            style={{ background: 'rgba(99,102,241,0.06)', border: '1px dashed rgba(99,102,241,0.3)', color: 'var(--color-primary)', fontFamily: 'var(--font-caption)' }}
          >
            <Icon name="Zap" size={11} color="var(--color-primary)" />
            Upgrade to Pro for 5-minute generations
          </button>
        )}
      </div>
      {/* Safe mode toggle */}
      <div className="flex items-center justify-between p-3 rounded-xl" style={{ background: 'var(--color-muted)', border: '1px solid var(--color-border)' }}>
        <div className="flex items-center gap-2">
          <Icon name="Shield" size={16} color="var(--color-success)" />
          <div>
            <p className="text-sm font-semibold" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>Safe Mode</p>
            <p className="text-xs" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>Filter harmful content</p>
          </div>
        </div>
        <button
          onClick={() => !disabled && onSafeModeChange(!safeMode)}
          disabled={disabled}
          aria-checked={safeMode}
          role="switch"
          className="relative w-11 h-6 rounded-full transition-all duration-200 disabled:opacity-50"
          style={{ background: safeMode ? 'var(--color-success)' : 'var(--color-input)' }}
        >
          <span
            className="absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform duration-200"
            style={{ transform: safeMode ? 'translateX(20px)' : 'translateX(0)' }}
          />
        </button>
      </div>
    </div>
  );
}